'use client';
import React, { useEffect, useRef, useState } from 'react';

interface StatItem {
  value: number;
  suffix: string;
  label: string;
  description: string;
}

const statsData: StatItem[] = [
  { value: 15, suffix: '+', label: 'Лет опыта', description: 'Работаем с 2011 года' },
  { value: 350, suffix: '+', label: 'Объектов', description: 'Сданных под ключ' },
  { value: 20000, suffix: '', label: 'м³ бетона', description: 'Залито за всё время' },
  { value: 100, suffix: '%', label: 'Гарантия', description: 'На каждый объект' },
];

function useCounter(target: number, duration: number, active: boolean) {
  const [count, setCount] = useState(0);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    if (!active) return;
    let start: number | null = null;

    const step = (timestamp: number) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) rafRef.current = requestAnimationFrame(step);
      else setCount(target);
    };

    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [active, target, duration]);

  return count;
}

function StatCard({ stat, active, index }: { stat: StatItem; active: boolean; index: number }) {
  const count = useCounter(stat.value, 1800, active);

  return (
    <div
      className={`reveal ${active ? 'active' : ''}`}
      style={{ transitionDelay: `${index * 0.1}s` }}
    >
      <div className="p-6 sm:p-8 md:p-10 border-r last:border-r-0 border-b md:border-b-0 [&:nth-child(2)]:border-r-0 md:[&:nth-child(2)]:border-r border-border group hover:bg-card transition-colors duration-500">
        <p className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter text-foreground leading-none mb-1">
          {count.toLocaleString('ru-RU')}
          <span className="text-primary">{stat.suffix}</span>
        </p>
        <p className="text-sm sm:text-base font-semibold text-foreground mt-2">{stat.label}</p>
        <p className="text-xs sm:text-sm text-muted-foreground mt-1">{stat.description}</p>
      </div>
    </div>
  );
}

export default function StatsSection() {
  const [active, setActive] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setActive(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-background border-y border-border relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-accent to-primary opacity-80" />

      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4">
          {statsData.map((stat, i) => (
            <StatCard key={i} stat={stat} active={active} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}